import React from "react";
import { deleteCreditCard, saveCreditCard } from "../api/RequesTixApi";
import { useAuth } from "../contexts/AuthContext";
import { BillingDTO, NewBillingDTO } from "../types/UserBilling";
import { createCssVariableString, getMonthsBetween, isAfter, lastDayOfMonth } from "best-common-react";

const defaultCC: NewBillingDTO = {
  userBillingId: undefined,
  firstName: "",
  lastName: "",
  countryId: 1,
  countryName: "United States",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  ccType: {},
  ccNickname: "",
  ccLast4: "",
  ccExp: "",
  ccToken: "",
  cvv: "",
  isDefault: false,
};

export const StatedRequiredCountryIds = [1, 2];

export type PermanentToken = {
  customerId: string;
  paymentInstrumentId?: string;
  instrumentIdentifierId?: string;
  ccLast4?: string;
  ccType?: string;
  ccExp?: string;
};

export const validCreditCard = (cc?: NewBillingDTO): boolean =>
  !!(
    !!cc?.firstName?.length &&
    !!cc?.lastName?.length &&
    !!cc?.addressLine1?.length &&
    !!cc?.countryId &&
    !!cc?.city?.length &&
    (!StatedRequiredCountryIds.includes(cc?.countryId) || cc?.state?.length) &&
    !!cc?.zip?.length &&
    !!cc?.phone?.length &&
    !!cc?.ccType &&
    !!cc?.ccNickname?.length &&
    !!cc?.ccExp?.length
  );

type UseCyberSource = {
  creditCard: BillingDTO;
  setCreditCard: (value: BillingDTO) => void;
  onSaveFromPermanentToken: (token: PermanentToken) => Promise<BillingDTO>;
  onCancel: () => void;
  onDelete: () => Promise<any>;
  makeDefault: () => Promise<BillingDTO>;
};

export default function useCyberSource(initial: any, otherUserId?: number): UseCyberSource {
  const { userInfo } = useAuth();
  const [creditCard, setCreditCard] = React.useState<BillingDTO>(initial || defaultCC);
  const [userId] = React.useState<number>(otherUserId || userInfo!.employeeUserId);

  const onSaveFromPermanentToken = async (token: PermanentToken): Promise<BillingDTO> => {
    const payload: BillingDTO = {
      ...creditCard,
      ccToken: "",
      ccLast4: token.ccLast4 || creditCard.ccLast4,
      ccExp: token.ccExp || creditCard.ccExp,
      ccNickname: creditCard.ccNickname,
      ccType: creditCard.ccType,
      customerId: token.customerId || token.paymentInstrumentId || token.instrumentIdentifierId || "",
    } as BillingDTO;
    return saveCreditCard(userId, payload).then((res) => {
      setCreditCard(res);
      return res;
    });
  };

  const onCancel = () => {
    setCreditCard(initial || defaultCC);
  };

  const onDelete = async () => {
    //@ts-expect-error
    return deleteCreditCard(userId, creditCard.userBillingId);
  };

  const makeDefault = async () => {
    return saveCreditCard(userId, { ...creditCard, isDefault: true });
  };

  return { creditCard, setCreditCard, onSaveFromPermanentToken, onCancel, onDelete, makeDefault };
}

export const getCCMonthsAway = (ccExp: string): number => {
  const split = ccExp.split("/");
  if (split.length > 1) {
    const now: Date = new Date();
    const exp: Date = lastDayOfMonth(new Date(`${split[0]}/1/${split[1]}`)) as Date;
    return isAfter(exp, now) ? getMonthsBetween(now, exp) : -1;
  } else {
    return 0;
  }
};

export const isCCExpired = (ccExp: string): boolean => {
  return getCCMonthsAway(ccExp) < 0;
};

export const getCCExpInfo = (ccExp: string): { color: string; text: string } => {
  const monthsAway = getCCMonthsAway(ccExp);
  if (monthsAway < 0) {
    return { color: createCssVariableString("coreColorsMlbRed"), text: "expired" };
  } else if (monthsAway < 6) {
    return { color: createCssVariableString("coreColorsNonTextRed"), text: "expiring" };
  }
  return { color: createCssVariableString("coreColorsOnColorsOnSurface"), text: "expires" };
};
